import React, { useEffect, useState, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { replace } from 'connected-react-router'
import { useInView } from 'react-intersection-observer'
import NavigableOfferContent from 'components/OfferPage/NavigableOfferContent/NavigableOfferContent'
import config from 'constants/config'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  PRODUCT_TYPE_ULTRALUX,
} from 'constants/offer'
import { fetchAlternativeOffers, fetchOfferById, fetchTraderInformation, offerViewed } from 'actions/OfferActions'
import { offerProductDetailView } from 'actions/AnalyticsActions'
import { RouteComponentProps, withRouter } from 'react-router'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferContentLoader from 'components/OfferPage/OfferContentLoader'
import { BottomMobileRelatedTravelItems } from 'components/OfferPage/RelatedTravelItems'
import OfferPageHeadData from 'components/OfferPage/OfferPageHeadData'
import Heading from 'LuxKit/Typography/Heading'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import {
  getPropertyBedbankOffer,
  getPropertyAlternateURL,
  getPropertyBestAlternateURL,
  getPropertyAlternateOfferIds,
} from 'selectors/offerPage/offerPageSelectors'
import { hasOrganicReferrer } from 'selectors/configSelectors'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import BundleAndSaveContent from './BundleAndSave/BundleAndSaveContent'
import UltraLuxContent from './UltraLux/UltraLuxContent'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { accommodationOfferClicked } from 'analytics/eventDefinitions'
import offerPageURL from 'lib/offer/offerPageURL'
import { isBundleOffer, isTourV1Offer } from 'lib/offer/offerTypes'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import BusinessTravellerAccommodationBudgetBarSwitch from 'businessTraveller/components/budget-bar/accommodation/BusinessTravellerAccommodationBudgetBarSwitch'
import zIndex from 'styles/tools/z-index'
import HttpStatus from 'components/Common/HttpStatus'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import LuxPlusHiddenOfferOfferContent from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferOfferContent'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import HowItWorksItems from 'components/Common/HowItWorks/HowItWorksItems'
import Group from 'components/utils/Group'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import useCurrentSiteTakeover from 'hooks/useCurrentSiteTakeover'
import SiteTakeoverOfferPageBanner from 'components/Common/SiteTakeover/SiteTakeoverOfferPageBanner'
import LastMinuteOfferContent from './LastMinute/LastMinuteOfferContent'
import FlashHotelContainer from './FlashHotel/FlashHotelContainer'
import PropertyContainer from './Property/PropertyContainer'
import AlwaysOnContainer from './AlwaysOn/AlwaysOnContainer'
import Sheet from 'components/utils/Sheet'
import useOffer from 'hooks/Offers/useOffer'

const StyledBusinessTravellerStickyBudgetBar = styled(BusinessTravellerAccommodationBudgetBarSwitch)`
  z-index: ${zIndex.floatingPanel};
`

const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`

const MainContent = styled(Group)`
  flex-grow: 1;
`

const ObservableWrapper = styled.div`
  padding-top: 1px;
  margin-top: ${rem(24)};
`

const StyledPane = styled(Pane)`
  position: relative;
`

interface Props extends RouteComponentProps<{
  offerSlug: string;
  offerId: string;
}> {
  offerId: string;
  traderInformation?: App.TraderInformation;
  regionCode: string;
  headlessMode: boolean;
  lereOfferStateAvailable: boolean;
  lereOfferCheckIn: string | undefined;
  lereOfferCheckOut: string | undefined;
  lereOfferRooms: Array<App.Occupants> | undefined;
  propertyAlternateOfferIds?: App.AlternativeOffers;
  propertyBedbankOffer?: App.BedbankOffer;
  propertyAlternateUrl?: string;
  propertyBestAlternateUrl?: string;
  canDoSoldoutRedirect: boolean;
  relatedTravelItems: App.OfferState['relatedTravelItems'];
  propertyPageOfferId?: string;
  currency: string;
  isSpoofed: boolean;
  utmMedium: string | undefined;
}

function OfferPage(props: Props) {
  const {
    offerId,
    match,
    headlessMode,
    lereOfferCheckIn,
    lereOfferCheckOut,
    lereOfferRooms,
    location: routerLocation,
    propertyAlternateOfferIds,
    propertyBedbankOffer,
    propertyAlternateUrl,
    propertyBestAlternateUrl,
    canDoSoldoutRedirect,
    relatedTravelItems,
    regionCode,
    traderInformation,
    propertyPageOfferId,
    currency,
    utmMedium,
  } = props

  const [offer, _, offerError] = useOffer<App.HotelOffer | App.VillaOffer | App.TourOffer>(offerId)
  const propertyId = offer?.property?.id
  const [showNavOfferContent, setShowNavOfferContent] = useState(false)
  const dataLoaded = useRef(false)
  const dispatch = useAppDispatch()
  const isMobileDevice = useIsMobileDevice()
  const isHiddenOffer = useAppSelector(state => getIsLuxPlusHiddenOffer(state, offer))

  const [propertyMapping, fetchingMapper] = usePropertyOfferMapping(propertyId, {
    disabled: offer?.type !== OFFER_TYPE_ALWAYS_ON || !config.ACCOMM_USE_LPC_PROPERTY_PAGE,
  })

  // This one is used to load the section before the user gets to it
  const [navigableRef, navigableInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  const setNavigableRef = useCallback<React.RefCallback<HTMLDivElement>>(
    (node) => {
      navigableRef(node)
    },
    [navigableRef],
  )

  useEffect(() => {
    if (offer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(accommodationOfferClicked(offer, currency))
      dataLoaded.current = true
    }
  }, [offer, currency])

  usePageActivityTracking()

  useEffect(() => {
    if (!showNavOfferContent && routerLocation.hash) {
      setShowNavOfferContent(true)
    }
  }, [routerLocation.hash]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showNavOfferContent) {
      window.location.href = routerLocation.hash
    }
  }, [showNavOfferContent]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (match.params.offerId) {
      dispatch(fetchOfferById(match.params.offerId))
    } else if (propertyPageOfferId) {
      dispatch(fetchOfferById(propertyPageOfferId))
    }
  }, [dispatch, match.params.offerId, propertyPageOfferId])

  useEffect(() => {
    if (offer && config.TRADER_INFORMATION_REGIONS && isEnabledForFeature(config.TRADER_INFORMATION_REGIONS, regionCode)) {
      dispatch(fetchTraderInformation(offer.id, regionCode))
    }
  }, [dispatch, offer?.id, regionCode]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (offer) {
      dispatch(offerProductDetailView(offer))
      window.dataLayer?.push({ offerType: offer.type })
    }
  }, [offer?.id, utmMedium]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (propertyId && !propertyAlternateOfferIds) {
      dispatch(fetchAlternativeOffers(propertyId, 'le'))
    } else if (!!propertyAlternateOfferIds?.bedbank?.length && !propertyBedbankOffer) {
      dispatch(fetchBedbankOfferById(propertyAlternateOfferIds.bedbank[0]))
    }
  }, [propertyId, propertyAlternateOfferIds, propertyBedbankOffer, dispatch])

  useEffect(() => {
    if (!!offer && ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type)) {
      dispatch(offerViewed(offer.id, offer.type, false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id, dispatch])

  const onDatePickerClose = useCallback(() => {
    if (routerLocation.hash) {
      dispatch(replace({ ...routerLocation, hash: undefined }))
    }
  }, [dispatch, routerLocation])

  const siteTakeover = useCurrentSiteTakeover({ vertical: 'hotel', offer: isTourV1Offer(offer) ? undefined : offer })

  if (offerError) {
    if (offerError.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <HttpStatus code={500}>
        <Error500Page error={offerError} />
      </HttpStatus>
    }
  }

  if (fetchingMapper || !offer) {
    return <OfferContentLoader />
  }

  if (isHiddenOffer) {
    return (
      <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
        <OfferPageHeadData offer={offer} isLuxPlusHiddenOffer />
        <LuxPlusHiddenOfferOfferContent offer={offer} />
      </AnalyticsPageContext.Provider>
    )
  }

  const isVilla = offer.type === 'rental'
  const breadCrumbItems = relatedTravelItems[offer?.id] || []
  const isBundleAndSave = isBundleOffer(offer)

  const {
    onlinePurchaseSchedule,
    type,
  } = offer

  const howItWorksItems = config.offerList.howItWorks[type as 'tour' | 'hotel' | 'last_minute_hotel']

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  let OfferPageContent: any
  if (offer.type === 'last_minute_hotel') {
    OfferPageContent = LastMinuteOfferContent
  } else if (offer.type === 'hotel' || offer.type === 'tactical_ao_hotel') {
    if (offer.property.isUltraLux) {
      OfferPageContent = UltraLuxContent
    } else if (offer.type === OFFER_TYPE_HOTEL) {
      OfferPageContent = FlashHotelContainer
    } else if (offer.type === OFFER_TYPE_ALWAYS_ON) {
      if (propertyMapping) {
        OfferPageContent = PropertyContainer
      } else {
        OfferPageContent = AlwaysOnContainer
      }
    }
  } else if (isBundleAndSave) {
    OfferPageContent = BundleAndSaveContent
  }
  if (!OfferPageContent) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  if (offer.slug !== match.params.offerSlug) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  if (offer.type !== 'hotel' && propertyBestAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyBestAlternateUrl} />
  }

  if (!availableForPurchase && canDoSoldoutRedirect && propertyAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyAlternateUrl} />
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <OfferPageHeadData offer={offer} />
    <StyledBusinessTravellerStickyBudgetBar
      checkInDate={lereOfferCheckIn}
      checkOutDate={lereOfferCheckOut}
      offer={offer}
      roomCount={lereOfferRooms?.length}
    />
    <OfferPageWrapper className="OfferPage">
      {!!siteTakeover?.offerTypes?.has(offer.type) && <SiteTakeoverOfferPageBanner />}
      <MainContent direction="vertical" verticalAlign="space-between">
        <LuxPlusLabelProvider page="offer-page">
          <OfferPageContent
            offer={offer}
            key={offer.id}
            onFilterClose={onDatePickerClose}
            shouldUseLereStickyFooter={false}
            lereYmalStickyFooterShowTrigger={false}
          />
        </LuxPlusLabelProvider>
          <ObservableWrapper ref={setNavigableRef}>
            {!isVilla && <StyledPane>
              {(navigableInView || showNavOfferContent) && (
                <>
                  <NavigableOfferContent
                    offerDetails={offer}
                    traderInformation={traderInformation}
                    hash={routerLocation.hash}
                    mobileHotelOfferPageTest={isMobileDevice}
                  />
                  {howItWorksItems && !offer.bundledWithFlightsOnly && !offer.disableBestPriceGuarantee && offer.productType !== PRODUCT_TYPE_ULTRALUX && <Sheet padding="40 0">
                    <LayoutContainer>
                      <Pane border="neutral-five" borderRadius="S" padding="40 24">
                        <Group direction="vertical" gap={32}>
                          <Heading variant="heading3" align="center">
                            Why you should book with us
                          </Heading>
                          <HowItWorksItems items={howItWorksItems} />
                        </Group>
                      </Pane>
                    </LayoutContainer>
                  </Sheet>}
                </>
              )}
            </StyledPane>}
          </ObservableWrapper>
          {!headlessMode &&
            <BottomMobileRelatedTravelItems offer={offer} items={breadCrumbItems} />}
      </MainContent>
    </OfferPageWrapper>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  const offerId = ownProps.propertyPageOfferId || ownProps.match?.params?.offerId || ''
  const offerSharedState = state.recommendations.offerSharedState[offerId]
  return {
    offerId,
    regionCode: state.geo.currentRegionCode,
    canDoSoldoutRedirect: IS_SSR && hasOrganicReferrer(state),
    traderInformation: state.offer.traderInformation[offerId]?.data,
    headlessMode: state.system.headlessMode,
    propertyAlternateOfferIds: getPropertyAlternateOfferIds(state, offerId),
    propertyBedbankOffer: getPropertyBedbankOffer(state, offerId),
    propertyAlternateUrl: getPropertyAlternateURL(state, offerId),
    propertyBestAlternateUrl: IS_SSR && getPropertyBestAlternateURL(state, offerId),
    relatedTravelItems: state.offer.relatedTravelItems,
    lereOfferStateAvailable: !!offerSharedState,
    lereOfferCheckIn: offerSharedState?.checkIn,
    lereOfferCheckOut: offerSharedState?.checkOut,
    lereOfferRooms: offerSharedState?.rooms,
    currency: state.geo.currentCurrency,
    isSpoofed: state.auth.account.isSpoofed,
    utmMedium: state.utm.utmMedium,
  }
}

type MappedValues = ReturnType<typeof mapStateToProps>

export default withRouter<Omit<Props, keyof MappedValues>, any>(connect(mapStateToProps)(OfferPage))
